/**
 * SOLID Review
 * S — Strong. One use case: the profile's nickname — saved, then kept as the server has it.
 * O — Strong. The round trip runs it; what is allowed is the Domain's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The decisions and auth-server ports and the round trip, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { ICommandNickname, IPayloadNickname, IPayloadProfile } from '../../domain/ports/schemas/AuthMoves';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { TServerRoundTrip, TAnswered } from '../shared/ServerRoundTrip';
import type { IConfigDepAuth } from './IConfigDepAuth';

/** The profile's nickname (FE3): saved on the server, then kept in the server's own words. */
export class ChangeNicknameUseCase implements IUseCase<ICommandNickname, TPortOutcome>, TServerRoundTrip<IPropsClientInput, ICommandNickname, IPayloadNickname, TAnswered<IPayloadProfile>> {
  constructor(private readonly deps: IConfigDepAuth) {}

  execute(ctx: IContextRequest, command: ICommandNickname): Promise<TPortOutcome> {
    return this.deps.trip.run(ctx, command, this);
  }

  plan(ctx: IContextRequest, input: IPropsClientInput, command: ICommandNickname) {
    return this.deps.decisions.planNickname(ctx, input, command);
  }

  send(ctx: IContextRequest, plan: IPayloadNickname) {
    return this.deps.auth.updateDisplayName(ctx, plan);
  }

  answer(ctx: IContextRequest, input: IPropsClientInput, _plan: IPayloadNickname, saved: TAnswered<IPayloadProfile>) {
    const changed: ICommandNickname = { displayName: saved.value.displayName };
    return this.deps.decisions.nicknameChanged(ctx, input, changed);
  }
}
